import { useQuery } from '@tanstack/react-query';
import { validatedApi } from '../../../lib/api/validated-client';
import type { AddonAvailabilityResponse, AddonItem } from '@lov/shared';

/**
 * Fetch available add-ons for a booking slot
 *
 * @param venueInventoryId - Primary venue inventory ID
 * @param date - Booking date (YYYY-MM-DD)
 * @param startTime - Start time (HH:MM)
 * @param endTime - End time (HH:MM)
 * @param options - Additional options
 * @returns Available add-ons with pricing and availability
 */
export function useAvailableAddons(
  venueInventoryId: string | null,
  date: string | null,
  startTime: string | null,
  endTime: string | null,
  options?: {
    enablePolling?: boolean;
  }
): {
  addons: AddonItem[];
  durationHours: number;
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
} {
  const { enablePolling = false } = options || {};

  const query = useQuery<AddonAvailabilityResponse, Error>({
    queryKey: ['addons-availability', venueInventoryId, date, startTime, endTime],
    queryFn: async () => {
      if (!venueInventoryId || !date || !startTime || !endTime) {
        throw new Error('Missing required parameters for addon availability');
      }

      const params = new URLSearchParams({
        date,
        startTime,
        endTime,
      });

      return validatedApi.get<AddonAvailabilityResponse>(
        `/bookings/inventory/${venueInventoryId}/addons?${params.toString()}`
      );
    },
    enabled: Boolean(venueInventoryId && date && startTime && endTime),
    staleTime: 10000, // 10 seconds - matches Phase 24 pattern
    refetchInterval: enablePolling ? 15000 : false, // 15-second polling when enabled
    refetchIntervalInBackground: false, // Pause when tab not visible
  });

  return {
    addons: query.data?.addons ?? [],
    durationHours: query.data?.durationHours ?? 0,
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
  };
}
